/**
 * DR Cart Settlement Coordinator
 *
 * Owns theme-side cart mutation ordering and exposes a single
 * busy/idle contract for checkout gating.
 */
(function () {
  'use strict';

  class PromiseQueue {
    constructor() {
      this._queue = Promise.resolve();
      this._pending = 0;
    }

    get isBusy() {
      return this._pending > 0;
    }

    enqueue(task) {
      this._pending++;
      dispatchState();
      const queuedTask = this._queue.catch(() => {}).then(() => task());
      this._queue = queuedTask.catch(() => {});
      return queuedTask.finally(() => {
        this._pending = Math.max(0, this._pending - 1);
        resolveIdleIfNeeded();
        dispatchState();
      });
    }
  }

  const queue = new PromiseQueue();
  const pendingIntents = new Map();
  let nextIntentId = 0;
  let idleResolvers = [];
  let activeMutationDepth = 0;

  function isBusy() {
    return queue.isBusy || pendingIntents.size > 0;
  }

  function getState() {
    return {
      busy: isBusy(),
      pendingIntents: pendingIntents.size,
      pendingMutations: queue._pending,
    };
  }

  function dispatchState() {
    document.dispatchEvent(
      new CustomEvent('dr:cart-settlement-state', {
        detail: getState(),
      })
    );
  }

  function resolveIdleIfNeeded() {
    if (isBusy()) return;
    idleResolvers.splice(0).forEach((resolve) => resolve());
  }

  function beginIntent(meta = {}) {
    const intentId = `dr-intent-${++nextIntentId}`;
    pendingIntents.set(intentId, {
      ...meta,
      createdAt: Date.now(),
    });
    dispatchState();
    return intentId;
  }

  function resolveIntent(intentId) {
    if (!intentId) return;
    if (!pendingIntents.delete(intentId)) return;
    resolveIdleIfNeeded();
    dispatchState();
  }

  function normalizeIntentIds(intentIds) {
    if (!intentIds) return [];
    return Array.isArray(intentIds) ? intentIds.filter(Boolean) : [intentIds].filter(Boolean);
  }

  function executeMutationTask(task, intentIds = []) {
    intentIds.forEach(resolveIntent);
    activeMutationDepth++;
    dispatchState();
    return Promise.resolve()
      .then(() => task())
      .finally(() => {
        activeMutationDepth = Math.max(0, activeMutationDepth - 1);
        resolveIdleIfNeeded();
        dispatchState();
      });
  }

  function enqueueMutation(task, opts = {}) {
    const intentIds = normalizeIntentIds(opts.intentIds);
    return queue.enqueue(() => executeMutationTask(task, intentIds));
  }

  function runNowOrEnqueue(task, opts = {}) {
    const intentIds = normalizeIntentIds(opts.intentIds);
    if (activeMutationDepth > 0) {
      return executeMutationTask(task, intentIds);
    }
    return queue.enqueue(() => executeMutationTask(task, intentIds));
  }

  async function whenIdle() {
    while (true) {
      const q1 = queue._queue;
      await q1;
      if (pendingIntents.size > 0) {
        await new Promise((resolve) => idleResolvers.push(resolve));
        continue;
      }
      const q2 = queue._queue;
      await q2;
      if (q2 === queue._queue && pendingIntents.size === 0) return;
    }
  }

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    pendingIntents.clear();
    idleResolvers.splice(0).forEach((resolve) => resolve());
    dispatchState();
  });

  window.drCartSettlementCoordinator = {
    beginIntent,
    resolveIntent,
    enqueueMutation,
    runNowOrEnqueue,
    whenIdle,
    get isBusy() {
      return isBusy();
    },
    get isInMutationContext() {
      return activeMutationDepth > 0;
    },
    get pendingIntentCount() {
      return pendingIntents.size;
    },
  };
})();
